import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { Clock } from 'lucide-react';

interface TimePickerProps {
  value: string; // "HH:mm" 24h format
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  /** "HH:mm" 24h format — 이 시간 이전은 선택 불가 */
  minTime?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

const to12Hour = (h24: number): { hour: number; period: 'AM' | 'PM' } => {
  const period: 'AM' | 'PM' = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return { hour: h12, period };
};

const to24Hour = (h12: number, period: 'AM' | 'PM') => {
  if (period === 'AM') return h12 === 12 ? 0 : h12;
  return h12 === 12 ? 12 : h12 + 12;
};

export function TimePicker({ value, onChange, disabled, className, placeholder, minTime }: TimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);

  // Parse value
  const [h24, m] = (value || '00:00').split(':').map(Number);
  const { hour: h12, period } = to12Hour(h24);

  const hours = Array.from({ length: 12 }, (_, i) => i + 1); // 1~12
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5); // 0,5,...,55

  // minTime 파싱
  const [minH24, minM] = minTime ? minTime.split(':').map(Number) : [-1, -1];

  const isHourDisabled = useCallback((hour12: number, p: 'AM' | 'PM') => {
    if (minH24 < 0) return false;
    const hour24 = to24Hour(hour12, p);
    return hour24 < minH24;
  }, [minH24]);

  const isMinuteDisabled = useCallback((minute: number) => {
    if (minH24 < 0) return false;
    if (h24 > minH24) return false;
    if (h24 < minH24) return true;
    return minute < minM;
  }, [minH24, minM, h24]);

  // Format display
  const displayValue = value ? `${period} ${pad(h12)}:${pad(m)}` : '';

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // 드롭다운 방향 결정: 아래 공간이 부족하면 위로 열기
  const handleToggle = useCallback(() => {
    if (disabled) return;
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dropdownHeight = 220;
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < dropdownHeight);
    }
    setIsOpen((prev) => !prev);
  }, [disabled, isOpen]);

  // Scroll to selected hour/minute when opening
  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      const scrollToActive = (ref: React.RefObject<HTMLDivElement | null>) => {
        const el = ref.current?.querySelector('[data-active="true"]');
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'instant' });
        }
      };
      scrollToActive(hourRef);
      scrollToActive(minuteRef);
    });
  }, [isOpen]);

  const handlePeriodChange = useCallback((newPeriod: 'AM' | 'PM') => {
    const newH24 = to24Hour(h12, newPeriod);
    onChange(`${pad(newH24)}:${pad(m)}`);
  }, [h12, m, onChange]);

  const handleHourChange = useCallback((newH12: number) => {
    const newH24 = to24Hour(newH12, period);
    onChange(`${pad(newH24)}:${pad(m)}`);
  }, [period, m, onChange]);

  const handleMinuteChange = useCallback((newMinute: number) => {
    onChange(`${pad(h24)}:${pad(newMinute)}`);
  }, [h24, onChange]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Input */}
      <button
        type="button"
        disabled={disabled}
        onClick={handleToggle}
        className={cn(
          'flex h-9 w-full items-center rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-1 text-sm shadow-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-orange-500',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isOpen && 'ring-1 ring-orange-500 border-orange-300',
        )}
      >
        <Clock className="h-3.5 w-3.5 text-neutral-400 mr-2 flex-shrink-0" />
        {displayValue ? (
          <span className="text-neutral-800 dark:text-neutral-200">{displayValue}</span>
        ) : (
          <span className="text-neutral-400 dark:text-neutral-500">{placeholder || '시간 선택'}</span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={cn(
          'absolute right-0 z-50 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 shadow-xl animate-in fade-in-0 zoom-in-95 duration-150 overflow-hidden w-[260px] max-w-[calc(100vw-2.5rem)]',
          openUpward ? 'bottom-full mb-1' : 'top-full mt-1',
        )}>
          <div className="flex divide-x divide-neutral-100 dark:divide-neutral-700">
            {/* AM/PM */}
            <div className="w-16 flex-shrink-0">
              <div className="text-[10px] text-neutral-400 font-medium text-center py-1 border-b border-neutral-50 dark:border-neutral-700">오전/오후</div>
              <div>
                {(['AM', 'PM'] as const).map((p) => {
                  // AM의 모든 시간이 minTime 이전이면 AM 비활성화
                  const allHoursDisabled = hours.every((h) => isHourDisabled(h, p));
                  return (
                    <button
                      key={p}
                      type="button"
                      disabled={allHoursDisabled}
                      className={cn(
                        'w-full py-1.5 text-sm text-center transition-colors',
                        allHoursDisabled
                          ? 'opacity-30 cursor-not-allowed'
                          : p === period
                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 font-semibold'
                            : 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700',
                      )}
                      onClick={() => handlePeriodChange(p)}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Hours */}
            <div className="flex-1">
              <div className="text-[10px] text-neutral-400 font-medium text-center py-1 border-b border-neutral-50 dark:border-neutral-700">시</div>
              <div ref={hourRef} className="max-h-[180px] overflow-y-auto">
                {hours.map((h) => {
                  const hourDisabled = isHourDisabled(h, period);
                  return (
                    <button
                      key={h}
                      type="button"
                      disabled={hourDisabled}
                      data-active={h === h12}
                      className={cn(
                        'w-full py-1.5 text-sm text-center transition-colors',
                        hourDisabled
                          ? 'opacity-30 cursor-not-allowed'
                          : h === h12
                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 font-semibold'
                            : 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700',
                      )}
                      onClick={() => handleHourChange(h)}
                    >
                      {h}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Minutes */}
            <div className="flex-1">
              <div className="text-[10px] text-neutral-400 font-medium text-center py-1 border-b border-neutral-50 dark:border-neutral-700">분</div>
              <div ref={minuteRef} className="max-h-[180px] overflow-y-auto">
                {minutes.map((min) => {
                  const minuteDisabled = isMinuteDisabled(min);
                  return (
                    <button
                      key={min}
                      type="button"
                      disabled={minuteDisabled}
                      data-active={min === m}
                      className={cn(
                        'w-full py-1.5 text-sm text-center transition-colors',
                        minuteDisabled
                          ? 'opacity-30 cursor-not-allowed'
                          : min === m
                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 font-semibold'
                            : 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700',
                      )}
                      onClick={() => handleMinuteChange(min)}
                    >
                      {pad(min)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
